/**
 * WooCommerce E-commerce Platform Tracker
 * Auto-detects and tracks e-commerce events from WooCommerce stores
 */
import BaseEcommerceTracker from './base';

export default class WooCommerceTracker extends BaseEcommerceTracker {
  constructor(b2mTracker, platformName) {
    super(b2mTracker, platformName);
    
    // WooCommerce-specific selectors
    this.selectors = {
      // Product page
      productDetail: '.product.type-product, .single-product, [data-product_id]',
      productName: '.product_title, .entry-title, h1.title',
      productPrice: '.price ins .amount, .price .amount, .woocommerce-Price-amount',
      productOldPrice: '.price del .amount',
      productImage: '.woocommerce-product-gallery__image img, .product-image img',
      productSku: '.sku, [data-sku]',
      productCategory: '.posted_in a, .product_meta .posted_in',
      productTags: '.tagged_as a',
      
      // Add to cart
      addToCartButton: '.single_add_to_cart_button, .add_to_cart_button, [data-add-to-cart]',
      quantityInput: '.quantity input.qty, input[name="quantity"]',
      variationSelect: '.variations select, .variation-selector',
      
      // Cart page
      cartPage: '.woocommerce-cart, .cart-page, [data-page="cart"]',
      cartItems: '.woocommerce-cart-form__cart-item, .cart_item',
      cartTotal: '.cart-subtotal .amount, .order-total .amount',
      cartItemRemove: '.remove, .product-remove a',
      cartQuantity: '.qty',
      
      // Checkout
      checkoutPage: '.woocommerce-checkout, .checkout-page',
      checkoutForm: 'form.checkout',
      billingForm: '.woocommerce-billing-fields',
      shippingForm: '.woocommerce-shipping-fields',
      paymentMethods: '.wc_payment_methods, #payment ul.payment_methods',
      placeOrderButton: '#place_order',
      
      // Order confirmation
      orderConfirmation: '.woocommerce-order-received, .order-received',
      orderId: '.woocommerce-order-overview__order strong, .order-number',
      orderTotal: '.woocommerce-order-overview__total strong, .order-total .amount',
      orderItems: '.woocommerce-table--order-details tbody tr',
      
      // Category/Shop pages
      shopPage: '.woocommerce-shop, .archive.woocommerce',
      productList: '.products, ul.products',
      productCard: '.product, li.product',
      
      // Search
      searchInput: '.woocommerce-product-search input[type="search"], .search-field',
      searchResults: '.woocommerce-result-count',
      
      // Mini cart
      miniCart: '.widget_shopping_cart, .mini-cart',
      miniCartItems: '.mini_cart_item',
      
      // Wishlist (YITH plugin)
      wishlistButton: '.add_to_wishlist, .yith-wcwl-add-button a',
      
      // Coupon
      couponInput: '#coupon_code, input[name="coupon_code"]',
      couponApply: '.coupon button, [name="apply_coupon"]'
    };
    
    // WooCommerce URL patterns
    this.urlPatterns = {
      product: ['/product/', '/?product='],
      category: ['/product-category/', '/shop/'],
      cart: ['/cart/', '/basket/'],
      checkout: ['/checkout/'],
      orderConfirmation: ['/order-received/', '/checkout/order-received/'],
      search: ['?s=', '&s='],
      account: ['/my-account/']
    };
  }

  /**
   * Initialize WooCommerce-specific tracking
   */
  init() {
    super.init();
    
    // Setup WooCommerce-specific tracking
    this.setupWooCommerceEventListeners();
    this.setupAddToCartTracking();
    this.setupCartUpdateTracking();
    this.setupVariationTracking();
    this.setupCheckoutTracking();
    this.setupCouponTracking();
    this.setupWishlistTracking();
    this.setupSearchTracking();
    
    // Intercept WooCommerce AJAX events
    this.interceptWooCommerceAjax();
    
    console.log('[B2M WooCommerce] ✅ WooCommerce-specific tracking initialized');
  }

  /**
   * Get page type from URL - WooCommerce specific
   */
  getPageType(path) {
    // Check body classes first (more reliable for WooCommerce)
    if (document.body.classList.contains('single-product')) return 'product';
    if (document.body.classList.contains('woocommerce-cart')) return 'cart';
    if (document.body.classList.contains('woocommerce-checkout')) return 'checkout';
    if (document.body.classList.contains('woocommerce-order-received')) return 'order-confirmation';
    if (document.body.classList.contains('archive') && document.body.classList.contains('woocommerce')) return 'category';
    if (document.body.classList.contains('search-results')) return 'search';
    
    // Fallback to URL patterns
    const url = path.toLowerCase();
    for (const [type, patterns] of Object.entries(this.urlPatterns)) {
      for (const pattern of patterns) {
        if (url.includes(pattern)) {
          return type;
        }
      }
    }
    
    return 'other';
  }

  /**
   * Auto-track product page
   */
  autoTrackProductPage() {
    // Try to get from wc_single_product_params or similar
    const product = this.getWooCommerceProductData();
    
    if (product) {
      this.trackViewItem([product], {
        page_type: 'product_detail',
        source: 'wc_data'
      });
    } else {
      this.waitForElement(this.selectors.productDetail, (container) => {
        const productData = this.extractWooProduct(container);
        if (productData.item_id || productData.item_name) {
          this.trackViewItem([productData], {
            page_type: 'product_detail',
            source: 'dom_extraction'
          });
        }
      });
    }
  }

  /**
   * Auto-track category/shop page
   */
  autoTrackCategoryPage() {
    this.waitForElement(this.selectors.productList, (container) => {
      const items = this.extractProductList(container);
      const categoryName = this.getCategoryName();
      
      if (items.length > 0) {
        this.trackViewItemList(items, categoryName, {
          page_type: 'category',
          source: 'auto_detect'
        });
      }
      
      this.setupProductCardClicks(container);
    });
  }

  /**
   * Auto-track cart page
   */
  autoTrackCartPage() {
    this.waitForElement(this.selectors.cartPage, (container) => {
      const items = this.extractCartItems(container);
      const total = this.extractCartTotal(container);
      
      if (items.length > 0) {
        this.trackViewCart(items, total, this.getCurrency(), {
          page_type: 'cart',
          source: 'auto_detect'
        });
      }
    });
  }

  /**
   * Auto-track checkout page
   */
  autoTrackCheckoutPage() {
    this.waitForElement(this.selectors.checkoutPage, (container) => {
      // Get cart data
      const cartData = this.getCartFromFragment() || { items: [], total: 0 };
      
      this.trackBeginCheckout(cartData.items, cartData.total, this.getCurrency(), {
        page_type: 'checkout',
        source: 'auto_detect'
      });
      
      // Setup shipping and payment tracking
      this.setupShippingInfoTracking();
      this.setupPaymentInfoTracking();
    });
  }

  /**
   * Auto-track order confirmation
   */
  autoTrackOrderConfirmation() {
    this.waitForElement(this.selectors.orderConfirmation, (container) => {
      const orderData = this.extractOrderData(container);
      
      if (orderData.transaction_id) {
        this.trackPurchase(orderData, {
          page_type: 'order_confirmation',
          source: 'auto_detect'
        });
      }
    });
  }

  /**
   * Auto-track search page
   */
  autoTrackSearchPage() {
    const searchTerm = new URLSearchParams(window.location.search).get('s');
    
    if (searchTerm) {
      const resultsEl = document.querySelector(this.selectors.searchResults);
      const resultsCount = resultsEl ? parseInt(resultsEl.textContent.match(/\d+/)?.[0]) : 0;
      
      this.trackSearch(searchTerm, {
        results_count: resultsCount,
        source: 'auto_detect'
      });
      
      this.waitForElement(this.selectors.productList, (container) => {
        const items = this.extractProductList(container);
        if (items.length > 0) {
          this.trackViewItemList(items, `Search: ${searchTerm}`, {
            search_term: searchTerm
          });
        }
      });
    }
  }

  // ============================================
  // WooCommerce-Specific Event Tracking
  // ============================================

  /**
   * Setup WooCommerce event listeners
   */
  setupWooCommerceEventListeners() {
    // WooCommerce triggers these jQuery events
    if (window.jQuery) {
      const $ = window.jQuery;
      
      // Cart updated
      $(document.body).on('updated_cart_totals', () => {
        this.handleCartUpdate();
      });
      
      // Added to cart (AJAX)
      $(document.body).on('added_to_cart', (e, fragments, hash, button) => {
        this.handleAddedToCart(button);
      });
      
      // Removed from cart
      $(document.body).on('removed_from_cart', (e, fragments, hash, button) => {
        this.handleRemovedFromCart(button);
      });
      
      // Checkout updated
      $(document.body).on('updated_checkout', () => {
        this.handleCheckoutUpdate();
      });
      
      // Variation found
      $(document.body).on('found_variation', (e, variation) => {
        this.handleVariationFound(variation);
      });
      
      // Payment method changed
      $(document.body).on('payment_method_selected', () => {
        this.handlePaymentMethodSelected();
      });
    }
  }

  /**
   * Setup add to cart tracking
   */
  setupAddToCartTracking() {
    document.addEventListener('click', (e) => {
      const button = e.target.closest(this.selectors.addToCartButton);
      if (!button) return;
      
      // Check if it's an AJAX add to cart (will be handled by jQuery event)
      if (button.classList.contains('ajax_add_to_cart')) {
        return; // Will be handled by added_to_cart event
      }
      
      // For non-AJAX (form submission)
      const form = button.closest('form.cart');
      if (form) {
        const product = this.extractProductFromForm(form);
        if (product.item_id || product.item_name) {
          this.trackAddToCart([product], product.price * (product.quantity || 1), this.getCurrency(), {
            source: 'button_click'
          });
        }
      }
    });
  }

  /**
   * Setup cart update tracking
   */
  setupCartUpdateTracking() {
    // Track quantity changes
    document.addEventListener('change', (e) => {
      const quantityInput = e.target.closest(this.selectors.cartQuantity);
      if (!quantityInput) return;
      
      const cartItem = quantityInput.closest(this.selectors.cartItems);
      if (cartItem) {
        const product = this.extractCartItemProduct(cartItem);
        product.quantity = parseInt(quantityInput.value) || 1;
        
        this.b2mTracker.track('cart_quantity_change', {
          ecommerce_platform: this.platformName,
          ...product
        });
      }
    });
    
    // Track remove clicks
    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest(this.selectors.cartItemRemove);
      if (!removeBtn) return;
      
      const cartItem = removeBtn.closest(this.selectors.cartItems);
      if (cartItem) {
        const product = this.extractCartItemProduct(cartItem);
        this.trackRemoveFromCart([product], {
          source: 'remove_button'
        });
      }
    });
  }

  /**
   * Setup variation tracking
   */
  setupVariationTracking() {
    document.addEventListener('change', (e) => {
      const select = e.target.closest(this.selectors.variationSelect);
      if (!select) return;
      
      // Variation change will be handled by found_variation event
    });
  }

  /**
   * Setup checkout tracking
   */
  setupCheckoutTracking() {
    // Track place order button click
    document.addEventListener('click', (e) => {
      const placeOrderBtn = e.target.closest(this.selectors.placeOrderButton);
      if (placeOrderBtn) {
        this.b2mTracker.track('checkout_submit', {
          ecommerce_platform: this.platformName,
          source: 'place_order_click'
        });
      }
    });
  }

  /**
   * Setup coupon tracking
   */
  setupCouponTracking() {
    document.addEventListener('click', (e) => {
      const applyBtn = e.target.closest(this.selectors.couponApply);
      if (!applyBtn) return;
      
      const couponInput = document.querySelector(this.selectors.couponInput);
      const couponCode = couponInput?.value?.trim();
      
      if (couponCode) {
        this.b2mTracker.track('coupon_apply', {
          ecommerce_platform: this.platformName,
          coupon_code: couponCode,
          source: 'button_click'
        });
      }
    });
  }

  /**
   * Setup wishlist tracking (YITH WooCommerce Wishlist)
   */
  setupWishlistTracking() {
    document.addEventListener('click', (e) => {
      const wishlistBtn = e.target.closest(this.selectors.wishlistButton);
      if (!wishlistBtn) return;
      
      const productContainer = wishlistBtn.closest(this.selectors.productDetail) ||
                               wishlistBtn.closest(this.selectors.productCard);
      
      if (productContainer) {
        const product = this.extractWooProduct(productContainer);
        this.trackAddToWishlist([product], {
          source: 'button_click'
        });
      }
    });
  }

  /**
   * Setup search tracking
   */
  setupSearchTracking() {
    const searchForm = document.querySelector('.woocommerce-product-search, form.search-form');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        const searchInput = searchForm.querySelector(this.selectors.searchInput);
        const searchTerm = searchInput?.value?.trim();
        
        if (searchTerm) {
          this.trackSearch(searchTerm, {
            source: 'form_submit'
          });
        }
      });
    }
  }

  /**
   * Intercept WooCommerce AJAX
   */
  interceptWooCommerceAjax() {
    // WooCommerce uses wc_cart_fragments_params for AJAX
    if (window.wc_cart_fragments_params) {
      // Already using jQuery events for this
    }
  }

  /**
   * Setup shipping info tracking
   */
  setupShippingInfoTracking() {
    const shippingMethods = document.querySelectorAll('input[name="shipping_method[0]"]');
    
    shippingMethods.forEach(method => {
      method.addEventListener('change', (e) => {
        const label = e.target.closest('li')?.querySelector('label')?.textContent?.trim() ||
                      e.target.value;
        
        this.trackAddShippingInfo({
          shipping_tier: label,
          value: this.getCheckoutTotal(),
          currency: this.getCurrency()
        });
      });
    });
  }

  /**
   * Setup payment info tracking
   */
  setupPaymentInfoTracking() {
    const paymentMethods = document.querySelectorAll('input[name="payment_method"]');
    
    paymentMethods.forEach(method => {
      method.addEventListener('change', (e) => {
        const label = document.querySelector(`label[for="${e.target.id}"]`)?.textContent?.trim() ||
                      e.target.value;
        
        this.trackAddPaymentInfo({
          payment_type: label,
          value: this.getCheckoutTotal(),
          currency: this.getCurrency()
        });
      });
    });
  }

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Handle added to cart (AJAX)
   */
  handleAddedToCart(button) {
    const productId = button?.dataset?.productId;
    const productSku = button?.dataset?.productSku;
    const quantity = parseInt(button?.dataset?.quantity) || 1;
    
    // Try to get product data
    const productCard = button?.closest(this.selectors.productCard);
    const product = productCard ? this.extractWooProduct(productCard) : {
      item_id: productId || productSku,
      item_name: button?.dataset?.productName,
      price: parseFloat(button?.dataset?.productPrice) || 0
    };
    
    product.quantity = quantity;
    
    if (product.item_id || product.item_name) {
      this.trackAddToCart([product], product.price * quantity, this.getCurrency(), {
        source: 'ajax_add_to_cart'
      });
    }
  }

  /**
   * Handle removed from cart
   */
  handleRemovedFromCart(button) {
    const productId = button?.dataset?.productId;
    const productSku = button?.dataset?.productSku;
    
    this.trackRemoveFromCart([{
      item_id: productId || productSku
    }], {
      source: 'ajax_remove'
    });
  }

  /**
   * Handle cart update
   */
  handleCartUpdate() {
    const items = this.extractCartItems(document);
    const total = this.extractCartTotal(document);
    
    this.b2mTracker.track('cart_update', {
      ecommerce_platform: this.platformName,
      items: items,
      value: total,
      currency: this.getCurrency()
    });
  }

  /**
   * Handle checkout update
   */
  handleCheckoutUpdate() {
    // Checkout was updated (shipping method changed, coupon applied, etc.)
    this.b2mTracker.track('checkout_update', {
      ecommerce_platform: this.platformName,
      value: this.getCheckoutTotal(),
      currency: this.getCurrency()
    });
  }

  /**
   * Handle variation found
   */
  handleVariationFound(variation) {
    if (variation) {
      this.b2mTracker.track('variant_change', {
        ecommerce_platform: this.platformName,
        variant_id: variation.variation_id,
        variant_sku: variation.sku,
        variant_price: variation.display_price,
        variant_attributes: variation.attributes,
        is_in_stock: variation.is_in_stock
      });
    }
  }

  /**
   * Handle payment method selected
   */
  handlePaymentMethodSelected() {
    const selectedMethod = document.querySelector('input[name="payment_method"]:checked');
    if (selectedMethod) {
      const label = document.querySelector(`label[for="${selectedMethod.id}"]`)?.textContent?.trim();
      
      this.trackAddPaymentInfo({
        payment_type: label || selectedMethod.value,
        value: this.getCheckoutTotal(),
        currency: this.getCurrency()
      });
    }
  }

  // ============================================
  // Data Extraction Methods
  // ============================================

  /**
   * Get product data from WooCommerce
   */
  getWooCommerceProductData() {
    // Check various WooCommerce global objects
    if (window.wc_single_product_params?.product) {
      return this.normalizeWooProduct(window.wc_single_product_params.product);
    }
    
    // Check for data in script tags (common in some themes)
    const productScript = document.querySelector('script.woocommerce-product-data, script[type="application/json"][data-product]');
    if (productScript) {
      try {
        const data = JSON.parse(productScript.textContent);
        return this.normalizeWooProduct(data);
      } catch (e) {}
    }
    
    // Try JSON-LD
    const jsonLd = document.querySelector('script[type="application/ld+json"]');
    if (jsonLd) {
      try {
        const data = JSON.parse(jsonLd.textContent);
        if (data['@type'] === 'Product') {
          return {
            item_id: data.sku,
            item_name: data.name,
            price: parseFloat(data.offers?.price) || 0,
            item_brand: data.brand?.name,
            currency: data.offers?.priceCurrency || this.getCurrency()
          };
        }
      } catch (e) {}
    }
    
    return null;
  }

  /**
   * Extract product from WooCommerce DOM
   */
  extractWooProduct(container) {
    const product = {
      item_id: null,
      item_name: null,
      price: 0,
      item_brand: null,
      item_category: null,
      currency: this.getCurrency()
    };
    
    // Get from data attributes
    product.item_id = container.dataset?.productId ||
                      container.querySelector('[data-product_id]')?.dataset?.product_id ||
                      container.classList.toString().match(/post-(\d+)/)?.[1];
    
    // Get name
    const nameEl = container.querySelector(this.selectors.productName);
    product.item_name = nameEl?.textContent?.trim();
    
    // Get price
    const priceEl = container.querySelector(this.selectors.productPrice);
    if (priceEl) {
      product.price = this.parsePrice(priceEl.textContent);
    }
    
    // Get SKU
    const skuEl = container.querySelector(this.selectors.productSku);
    if (skuEl && !product.item_id) {
      product.item_id = skuEl.textContent.trim();
    }
    
    // Get category
    const categoryEl = container.querySelector(this.selectors.productCategory);
    if (categoryEl) {
      product.item_category = categoryEl.textContent.trim();
    }
    
    return product;
  }

  /**
   * Extract product from add to cart form
   */
  extractProductFromForm(form) {
    const product = this.getWooCommerceProductData() || {};
    
    // Get product ID
    const productIdInput = form.querySelector('input[name="product_id"], button[name="add-to-cart"]');
    product.item_id = product.item_id || productIdInput?.value;
    
    // Get variation ID if present
    const variationInput = form.querySelector('input[name="variation_id"]');
    if (variationInput?.value) {
      product.variant_id = variationInput.value;
    }
    
    // Get quantity
    const quantityInput = form.querySelector(this.selectors.quantityInput);
    product.quantity = parseInt(quantityInput?.value) || 1;
    
    return product;
  }

  /**
   * Extract products from list
   */
  extractProductList(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    const items = [];
    
    productCards.forEach((card, index) => {
      const product = this.extractWooProduct(card);
      product.index = index;
      
      // Try to get from add to cart button
      const addToCartBtn = card.querySelector(this.selectors.addToCartButton);
      if (addToCartBtn) {
        product.item_id = product.item_id || addToCartBtn.dataset?.productId;
        product.item_name = product.item_name || addToCartBtn.dataset?.productName;
        product.price = product.price || parseFloat(addToCartBtn.dataset?.productPrice) || 0;
      }
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract cart items
   */
  extractCartItems(container) {
    const cartItems = container.querySelectorAll(this.selectors.cartItems);
    const items = [];
    
    cartItems.forEach((item, index) => {
      const product = this.extractCartItemProduct(item);
      product.index = index;
      
      if (product.item_id || product.item_name) {
        items.push(product);
      }
    });
    
    return items;
  }

  /**
   * Extract product from cart item row
   */
  extractCartItemProduct(cartItem) {
    const product = {
      item_id: cartItem.dataset?.productId,
      item_name: null,
      price: 0,
      quantity: 1
    };
    
    // Get name from link
    const nameLink = cartItem.querySelector('.product-name a, .cart-item-name a');
    product.item_name = nameLink?.textContent?.trim();
    
    // Get price
    const priceEl = cartItem.querySelector('.product-price .amount, .cart-item-price .amount');
    if (priceEl) {
      product.price = this.parsePrice(priceEl.textContent);
    }
    
    // Get quantity
    const quantityInput = cartItem.querySelector(this.selectors.cartQuantity);
    product.quantity = parseInt(quantityInput?.value) || 1;
    
    // Get from remove button data
    const removeBtn = cartItem.querySelector(this.selectors.cartItemRemove);
    if (removeBtn) {
      product.item_id = product.item_id || removeBtn.dataset?.productId;
      product.item_name = product.item_name || removeBtn.dataset?.productName;
    }
    
    return product;
  }

  /**
   * Extract cart total
   */
  extractCartTotal(container) {
    const totalEl = container.querySelector(this.selectors.cartTotal);
    if (totalEl) {
      return this.parsePrice(totalEl.textContent);
    }
    return 0;
  }

  /**
   * Extract order data from confirmation page
   */
  extractOrderData(container) {
    const orderData = {
      transaction_id: null,
      value: 0,
      currency: this.getCurrency(),
      items: [],
      tax: 0,
      shipping: 0
    };
    
    // Get order ID
    const orderIdEl = container.querySelector(this.selectors.orderId);
    orderData.transaction_id = orderIdEl?.textContent?.trim();
    
    // Get total
    const totalEl = container.querySelector(this.selectors.orderTotal);
    if (totalEl) {
      orderData.value = this.parsePrice(totalEl.textContent);
    }
    
    // Get items from order table
    const orderItems = container.querySelectorAll(this.selectors.orderItems);
    orderItems.forEach(row => {
      const nameEl = row.querySelector('.product-name');
      const quantityMatch = nameEl?.textContent?.match(/×\s*(\d+)/);
      const priceEl = row.querySelector('.product-total .amount');
      
      if (nameEl) {
        orderData.items.push({
          item_name: nameEl.textContent.replace(/×\s*\d+/, '').trim(),
          quantity: quantityMatch ? parseInt(quantityMatch[1]) : 1,
          price: priceEl ? this.parsePrice(priceEl.textContent) : 0
        });
      }
    });
    
    // Try to get from WooCommerce data
    if (window.wc_order_review_params?.order) {
      const wcOrder = window.wc_order_review_params.order;
      orderData.transaction_id = orderData.transaction_id || wcOrder.id;
      orderData.value = orderData.value || wcOrder.total;
      orderData.tax = wcOrder.tax || 0;
      orderData.shipping = wcOrder.shipping || 0;
    }
    
    return orderData;
  }

  /**
   * Get cart from WooCommerce fragments
   */
  getCartFromFragment() {
    if (window.wc_cart_fragments_params?.cart) {
      return window.wc_cart_fragments_params.cart;
    }
    return null;
  }

  /**
   * Get category name
   */
  getCategoryName() {
    // From breadcrumb
    const breadcrumb = document.querySelector('.woocommerce-breadcrumb');
    if (breadcrumb) {
      const items = breadcrumb.querySelectorAll('a');
      if (items.length > 1) {
        return items[items.length - 1].textContent.trim();
      }
    }
    
    // From archive title
    const archiveTitle = document.querySelector('.woocommerce-products-header__title');
    if (archiveTitle) {
      return archiveTitle.textContent.trim();
    }
    
    return 'Shop';
  }

  /**
   * Get currency
   */
  getCurrency() {
    return window.woocommerce_params?.currency || 
           window.wc_cart_params?.currency ||
           document.querySelector('.woocommerce-Price-currencySymbol')?.textContent?.trim() ||
           'USD';
  }

  /**
   * Get checkout total
   */
  getCheckoutTotal() {
    const totalEl = document.querySelector('.order-total .amount');
    return totalEl ? this.parsePrice(totalEl.textContent) : 0;
  }

  /**
   * Normalize WooCommerce product
   */
  normalizeWooProduct(product) {
    return {
      item_id: product.id?.toString() || product.sku,
      item_name: product.name || product.title,
      price: parseFloat(product.price) || 0,
      item_brand: product.brand,
      item_category: product.category || product.categories?.[0],
      currency: product.currency || this.getCurrency()
    };
  }

  /**
   * Setup product card click tracking
   */
  setupProductCardClicks(container) {
    const productCards = container.querySelectorAll(this.selectors.productCard);
    
    productCards.forEach((card, index) => {
      card.addEventListener('click', (e) => {
        if (e.target.closest(this.selectors.addToCartButton) ||
            e.target.closest(this.selectors.wishlistButton)) {
          return;
        }
        
        const product = this.extractWooProduct(card);
        product.index = index;
        
        if (product.item_id || product.item_name) {
          this.trackSelectItem([product], {
            source: 'product_card_click'
          });
        }
      });
    });
  }

  /**
   * Wait for element
   */
  waitForElement(selector, callback, timeout = 5000) {
    const element = document.querySelector(selector);
    
    if (element) {
      callback(element);
      return;
    }
    
    const observer = new MutationObserver((mutations, obs) => {
      const el = document.querySelector(selector);
      if (el) {
        obs.disconnect();
        callback(el);
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    setTimeout(() => observer.disconnect(), timeout);
  }
}
